import React, { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { RegressionScenario } from "./regression.types";
import { REGRESSION_COLORS } from "./regressionStatus";

interface Props {
  scenarios: RegressionScenario[];
}

export const RegressionTagCoverage: React.FC<Props> = ({ scenarios }) => {
  const tags = useMemo(() => {
    const byTag = new Map<string, { total: number; failing: number }>();
    for (const s of scenarios) {
      const failing = s.result !== "SUCCESS";
      for (const tag of s.tags) {
        const entry = byTag.get(tag) ?? { total: 0, failing: 0 };
        entry.total += 1;
        if (failing) entry.failing += 1;
        byTag.set(tag, entry);
      }
    }
    return Array.from(byTag.entries())
      .map(([tag, v]) => ({ tag, ...v }))
      .sort((a, b) => b.total - a.total);
  }, [scenarios]);

  if (tags.length === 0) {
    return <Typography variant="body2" color="text.secondary">No tags found in the latest run.</Typography>;
  }

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 1.125 }}>
      {tags.map(({ tag, total, failing }) => (
        <Paper
          key={tag}
          variant="outlined"
          sx={{ display: "flex", alignItems: "center", gap: 1.125, px: 1.25, py: 1, borderRadius: "7px", bgcolor: "action.hover" }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", flex: "none", bgcolor: failing > 0 ? REGRESSION_COLORS.failed : REGRESSION_COLORS.passed }} />
          <Typography variant="caption" fontFamily="monospace" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            @{tag}
          </Typography>
          <Typography variant="caption" fontFamily="monospace" color="text.secondary" sx={{ flex: "none" }}>
            {total - failing}/{total}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};

export default RegressionTagCoverage;
